import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Plus, X } from "lucide-react";

import { cn } from "@/infrastructure/lib/utils";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { useMagicFill } from "@/shared/contexts/MagicFillContext";
import type { FacetFilter } from "@/features/Facets/types";

/**
 * As etiquetas de uma skill do CATÁLOGO (AB#85634, AC27).
 *
 * === Por que não é o `FacetSelector` da organização ===
 *
 * Aquele oferece o vocabulário de UMA organização, e a skill de plataforma não
 * pertence a nenhuma: ela é instalada por muitas, cada uma com o seu cadastro.
 * Somar os vocabulários de todas traria o mesmo par repetido por organização —
 * foi o que apareceu na tela, com `marketing` e `playbook` duplicados.
 *
 * Então aqui a estrutura é LIVRE: quem registra declara os pares que a skill
 * tem, e é a INSTALAÇÃO que registra cada um como facet na organização que
 * instalou (o caminho que o AC27 já entrega). O vocabulário existente entra
 * apenas como sugestão — o que houver, sem duplicata —, para quem registra
 * seguir a estrutura que as organizações já usam em vez de inventar sinônimos.
 *
 * A apresentação é a do seletor de facetas: agrupado por dimensão, com marca no
 * que está escolhido e as etiquetas em chips removíveis.
 */
export const SkillFacetsField = ({
  value,
  onChange,
  sugestoes = [],
  fieldKey = "facets",
}: {
  value: FacetFilter[];
  onChange: (proximas: FacetFilter[]) => void;
  /**
   * O que a lista oferece: o vocabulário das skills mais o que a organização já
   * usa. É sugestão, não cerca — o campo aceita qualquer par declarado abaixo.
   */
  sugestoes?: Array<{ name: string; value: string }>;
  /** Liga o spinner do Magic Fill, como nos demais campos. */
  fieldKey?: string;
}) => {
  const { t } = useTranslation();
  const { isGlobalLoading, isFieldLoading } = useMagicFill();
  const carregando = isGlobalLoading || isFieldLoading(fieldKey);

  const [aberto, setAberto] = useState(false);
  const [nome, setNome] = useState("");
  const [valor, setValor] = useState("");

  /**
   * As sugestões, sem repetição.
   *
   * O mesmo par existe uma vez por organização — e, dentro de uma, uma vez por
   * origem (`source`), porque a unicidade do banco inclui a origem. Para quem
   * escolhe, `area:marketing` é uma opção só.
   */
  const porDimensao = useMemo(() => {
    const vistos = new Set<string>();
    const grupos = new Map<string, string[]>();

    for (const facet of sugestoes) {
      const chave = `${facet.name}:${facet.value}`;
      if (vistos.has(chave)) continue;
      vistos.add(chave);

      grupos.set(facet.name, [...(grupos.get(facet.name) ?? []), facet.value]);
    }

    return [...grupos.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [sugestoes]);

  const escolhida = (name: string, valorDaFacet: string) =>
    value.some((item) => item.name === name && item.value === valorDaFacet);

  const alternar = (name: string, valorDaFacet: string) =>
    onChange(
      escolhida(name, valorDaFacet)
        ? value.filter((item) => !(item.name === name && item.value === valorDaFacet))
        : [...value, { name, value: valorDaFacet }],
    );

  /** Identificador, não texto de tela: minúsculo, sem acento e sem espaço. */
  const normaliza = (bruto: string) =>
    bruto
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const acrescentar = () => {
    const name = normaliza(nome);
    const valorNovo = normaliza(valor);
    if (!name || !valorNovo || escolhida(name, valorNovo)) return;

    onChange([...value, { name, value: valorNovo }]);
    setValor("");
  };

  return (
    <div className="space-y-2">
      <Popover open={aberto} onOpenChange={setAberto}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={carregando}
            className="w-full justify-between font-normal"
          >
            {value.length > 0
              ? t("skills.publish.facetsChosen", { count: value.length })
              : t("skills.publish.facetsChoose")}
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0">
          <div className="scroll-slim max-h-[280px] overflow-y-auto p-2">
            {porDimensao.length === 0 && (
              <p className="px-2 py-1.5 text-xs text-muted-foreground">
                {t("skills.publish.facetsNoVocabulary")}
              </p>
            )}

            {porDimensao.map(([dimensao, valores]) => (
              <div key={dimensao} className="mb-2 last:mb-0">
                <p className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {dimensao}
                </p>
                {valores.sort().map((valorDaFacet) => (
                  <button
                    key={valorDaFacet}
                    type="button"
                    onClick={() => alternar(dimensao, valorDaFacet)}
                    className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
                  >
                    <span className="truncate">{valorDaFacet}</span>
                    {escolhida(dimensao, valorDaFacet) && (
                      <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>

          {/* A estrutura é livre: a skill declara o que ela é, e a instalação
              registra o par na organização que instalou. O vocabulário acima é
              sugestão, não cerca. */}
          <div className="flex items-center gap-1.5 border-t p-2">
            <Input
              value={nome}
              onChange={(evento) => setNome(evento.target.value)}
              placeholder={t("skills.publish.facetName")}
              className="h-8"
            />
            <span className="text-muted-foreground">:</span>
            <Input
              value={valor}
              onChange={(evento) => setValor(evento.target.value)}
              onKeyDown={(evento) => {
                if (evento.key !== "Enter") return;
                evento.preventDefault();
                acrescentar();
              }}
              placeholder={t("skills.publish.facetValue")}
              className="h-8"
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={acrescentar}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((etiqueta) => (
            <Badge
              key={`${etiqueta.name}:${etiqueta.value}`}
              variant="secondary"
              className={cn("cursor-pointer gap-1 text-[11px] font-normal")}
              onClick={() => alternar(etiqueta.name, etiqueta.value)}
            >
              <span className="text-muted-foreground">{etiqueta.name}:</span>
              {etiqueta.value}
              <X className="h-3 w-3" />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};
